/**
 * PageShell — the standard full-page frame for scrollable "document" tabs.
 *
 * Guarantees every main tab shares one identical header: a full-width,
 * flush-left top bar (title + description + optional action) with a bottom
 * rule, followed by the page's content in a scroll region flush to the same
 * left edge.
 *
 * Use this for list/document pages (Sheets, Files, Users, Agents, SEO,
 * Scraper, …). Full-height app shells that own their own layout (Support's
 * 3-pane inbox, the calendars) compose the same header bar inline instead.
 */
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

import { PageHeader } from './PageHeader';

export interface PageShellProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  /** Extra classes for the scrollable content region. */
  contentClassName?: string;
}

export function PageShell({
  title,
  description,
  action,
  children,
  contentClassName,
}: PageShellProps): JSX.Element {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-rule bg-paper px-6 py-3">
        <PageHeader
          title={title}
          description={description}
          action={action}
          className="mb-0"
        />
      </div>
      <div
        className={cn(
          // scrollbar-gutter:stable reserves the scrollbar space so the content
          // width doesn't oscillate when the vertical scrollbar toggles. That
          // oscillation is what drives recharts ResponsiveContainer into a
          // ResizeObserver feedback loop on chart-heavy pages (e.g. SEO).
          'min-h-0 flex-1 overflow-y-auto px-6 py-8 [scrollbar-gutter:stable]',
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
